import { useEffect, useRef, useState, type ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { safeAuthReturnTo } from "@playbookai/auth-client/return-to";
import { Button, EmptyState } from "@playbookai/ui";
import { ME_QUERY_KEY } from "./auth-store.js";
import { authProductMark, SignInElsewhereScreen } from "./auth-screens.js";
import {
  claimAutoRestart,
  forgetPasswordChangeMarker,
  forgetReturnTo,
  keepReturnTo,
  takePasswordChangeMarker,
  takeReturnTo,
} from "./auth-tab-storage.js";
import {
  AuthCallbackError,
  beginLogin,
  completeCallback,
  type BeginLoginOptions,
  type CallbackResult,
} from "./session-api.js";
import {
  announceSignedIn,
  electSignInLeader,
  releaseSignInLease,
  takeSignInLease,
} from "./sign-in-lease.js";

/**
 * Mounted on `/auth/callback`. The BFF (services/auth) has its own
 * `/api/auth/callback` endpoint that receives Keycloak's redirect, exchanges
 * the code, mints a session cookie, and returns `{ returnTo, csrfToken }`.
 *
 * TWO flows land here (PB-5415), because the confidential BFF client has
 * exactly one registered redirect URI in every environment. A self-service
 * password change comes back with an `outcome` and no session: a completed one
 * stops on this route to say so, and a cancelled one navigates back to where it
 * started with nothing changed.
 *
 * This component:
 *   1. Calls the BFF's callback endpoint with the current URL's query string.
 *   2. On success, resets the cached /me query to "checking" and navigates to
 *      the page this tab kept before it left, else `returnTo` (the path
 *      captured before the redirect): a recovered session answers Home, but
 *      this tab still knows where it was going (PB-5461, d21). A sign-in also
 *      gives up the one-tab sign-in lease and tells the waiting tabs.
 *   3. On failure, decides ONCE per callback what happens next (PB-5461,
 *      CONTRACT §1):
 *      - a lost or spent sign-in (`oidc_login_expired`, `oidc_callback_invalid`)
 *        restarts by itself to the page this tab kept, at most once per tab
 *        per two minutes, unless another tab is signing in right now: then
 *        it waits for that tab instead (d21); a second failure shows a
 *        message and Try again;
 *      - a refusal shows its reason and "Choose another account";
 *      - anything else is a server problem with Try again;
 *      - a password change keeps PB-5415's own words and never restarts.
 *
 * Every other terminal failure stays on this route. Navigating home
 * automatically would mount RequireAuth and immediately begin another login,
 * turning one failure into a redirect loop and eventually a 429 response.
 */

/** The codes that mean "this sign-in's ticket is gone; a new one will work". */
const RESTARTABLE_CODES: ReadonlySet<string> = new Set([
  "oidc_login_expired",
  "oidc_callback_invalid",
]);

/** The app refused this person. Each gets its own words (PLAN §7). `null`
 *  description: show the server's own message. */
const REFUSALS = {
  oidc_identity_not_provisioned: {
    title: "You don’t have access yet.",
    description:
      "Your account isn’t added to this workspace. Ask your admin to add you, or choose another account.",
  },
  oidc_access_not_ready: {
    title: "Your access isn’t fully set up yet.",
    description: "Ask your admin to finish setting up your roles.",
  },
  oidc_use_company_sign_in: {
    title: "Use your company sign-in.",
    description:
      "This account signs in with Microsoft. Choose another account, then press Sign in with Microsoft.",
  },
  local_accounts_disabled: {
    title: "Password sign-in is turned off.",
    description: null,
  },
  identity_collision: {
    title: "We can’t match your account.",
    description:
      "Your sign-in is linked to a different account. Ask your admin for help.",
  },
} as const satisfies Record<
  string,
  { readonly title: string; readonly description: string | null }
>;

type RefusalCode = keyof typeof REFUSALS;

function isRefusal(code: string): code is RefusalCode {
  return Object.hasOwn(REFUSALS, code);
}

/** What a failed callback leaves on screen. */
type FailureScreen =
  /** A restartable failure that may not restart again (yet). */
  | { readonly kind: "expired"; readonly returnTo: string }
  /** A lost ticket while a password change from this tab is still live. */
  | { readonly kind: "password-change-unconfirmed" }
  /** PB-5415's own `password_change_*` answers. */
  | { readonly kind: "password-change"; readonly pending: boolean }
  | {
      readonly kind: "refused";
      readonly code: RefusalCode;
      readonly message: string;
    }
  | { readonly kind: "server-problem"; readonly returnTo: string };

type CallbackOutcome =
  | {
      readonly kind: "completed";
      readonly result: CallbackResult;
      /** The page this tab kept before it left, if a safe one. */
      readonly keptReturnTo: string | null;
    }
  /** The tab is already on its way back to Keycloak. */
  | { readonly kind: "restarting" }
  /** Another tab is signing in; this one waits for its session (d21). */
  | { readonly kind: "waiting"; readonly returnTo: string }
  | { readonly kind: "failed"; readonly screen: FailureScreen };

/**
 * The one in-flight-or-finished callback exchange, keyed by its query string.
 *
 * Module scope, deliberately. A StrictMode remount builds fresh refs, so an
 * instance-scoped guard cannot recognise a callback another instance already
 * started, and the effect calls the BFF a second time. For a sign-in that is
 * harmless — the consumed state falls through to the BFF's recovery branch,
 * which returns the session it just minted. A completed password change has
 * REVOKED every session, so there is nothing to recover with: the duplicate
 * correctly answers `oidc_login_expired`, and the screen would then tell someone
 * their sign-in expired moments after their password successfully changed.
 *
 * Holding the PROMISE rather than an "already settled" flag is what closes the
 * race. A flag set after the response still loses when the first instance
 * unmounts mid-flight: the second instance sends its own request, and because
 * the first one is doing the slow work — revoking sessions, writing audit — the
 * second one's REJECTION can arrive first and paint the error over the success.
 * A remount now awaits the SAME exchange instead of starting a rival one.
 *
 * The promise carries the DECISION, not only the answer (PB-5461): reading the
 * tab records, claiming the automatic restart and starting it happen once per
 * callback inside it, so two instances can never both restart, nor can one
 * restart while the other reads the claim it just made as a second failure.
 *
 * Keyed by the query string so it can only ever replay the same callback, never
 * mask a later, genuinely different one.
 */
let callbackExchange: {
  search: string;
  promise: Promise<CallbackOutcome>;
} | null = null;

/** What the success screen has to tell the person, once the change is done. */
interface PasswordChangeReport {
  readonly signOut: "revoked" | "incomplete";
}

/**
 * Clear the record above between tests.
 *
 * Module state outlives a render, which is the whole point in the browser — a
 * remount must join the exchange already running — but in a test file it also
 * outlives the test, so one spec's exchange would satisfy the next spec's
 * lookup. Exported for that reason only; nothing in the app calls it.
 */
export function __resetCallbackExchangeForTests(): void {
  callbackExchange = null;
}

async function exchangeCallback(search: string): Promise<CallbackOutcome> {
  /* The flow this callback ends. Only the one the sign-in lease names may give
     the lease up or restart in it: a page that never started that flow, such
     as a copied tab, has no say over it (PR 407 review round 2 F1). */
  const flowState = new URLSearchParams(search).get("state") || null;
  let result: CallbackResult;
  try {
    result = await completeCallback(search);
  } catch (error) {
    return decideFailure(error, flowState);
  }
  // Any answer ends whatever this tab had in flight (AC-21). The kept page is
  // read and removed here, once per callback, and goes before the server's
  // `returnTo`, which a recovered session answers with Home (d21).
  forgetPasswordChangeMarker();
  const keptReturnTo = takeReturnTo();
  if (result.outcome === undefined) {
    // A sign-in: the waiting tabs can stop waiting (d21).
    releaseSignInLease(flowState);
    announceSignedIn();
  }
  return { kind: "completed", result, keptReturnTo };
}

async function decideFailure(
  error: unknown,
  flowState: string | null,
): Promise<CallbackOutcome> {
  console.warn("[auth] callback failed");
  // Read and cleared on every answer, so an abandoned change never holds back
  // a later sign-in's restart.
  const passwordChangeLive = takePasswordChangeMarker();
  const code =
    error instanceof AuthCallbackError ? error.code : "auth_callback_failed";

  if (code.startsWith("password_change_")) {
    return failed({
      kind: "password-change",
      pending: code === "password_change_confirmation_pending",
    });
  }

  if (RESTARTABLE_CODES.has(code)) {
    /* A lost ticket under a live password change must not restart: a reload
       during the return can lose the state AFTER the password changed, so
       neither "failed" nor a silent sign-in would be honest (PB-5461). */
    if (passwordChangeLive)
      return failed({ kind: "password-change-unconfirmed" });
    const returnTo = takeReturnTo() ?? "/";
    if (claimAutoRestart()) {
      /* One tab at a time goes to Keycloak (d21). Claimed first, so a tab that
         wins the lease always uses it. The flow the lease names restarts in
         it; a tab that loses keeps its page for a reload, and waits for the
         other tab's session. */
      if (!(await electSignInLeader({ flowState }))) {
        keepReturnTo(returnTo);
        return { kind: "waiting", returnTo };
      }
      try {
        // beginLogin keeps `returnTo` again, so a second failure still has it.
        window.location.replace(await beginLogin(returnTo));
        return { kind: "restarting" };
      } catch {
        // Could not even start: the screen below offers Try again.
      }
    }
    return failed({ kind: "expired", returnTo });
  }

  if (isRefusal(code) && error instanceof AuthCallbackError) {
    return failed({ kind: "refused", code, message: error.message });
  }
  return failed({ kind: "server-problem", returnTo: takeReturnTo() ?? "/" });
}

function failed(screen: FailureScreen): CallbackOutcome {
  return { kind: "failed", screen };
}

/** The product mark at the top of the column, on every state of this page. */
const productMark = authProductMark;

export function CallbackPage(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const retryInFlight = useRef(false);
  const [failure, setFailure] = useState<FailureScreen | null>(null);
  /** The kept page, while another tab signs in for this one (d21). */
  const [waitingFor, setWaitingFor] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const [passwordChanged, setPasswordChanged] =
    useState<PasswordChangeReport | null>(null);

  useEffect(() => {
    /* JOIN the exchange for this exact callback rather than starting a rival
       one. A remount that issued its own request would race the first: the
       first does the slow work, so the second's rejection can land first and
       paint an expiry message over a successful change.

       `live` is per effect run, so only the mounted instance acts on the
       outcome: StrictMode's discarded first run, or an instance unmounted
       mid-flight, neither navigates nor paints. */
    let live = true;
    if (callbackExchange?.search !== location.search) {
      callbackExchange = {
        search: location.search,
        promise: exchangeCallback(location.search),
      };
    }

    void callbackExchange.promise.then((outcome) => {
      if (!live || outcome.kind === "restarting") return;
      if (outcome.kind === "failed" || outcome.kind === "waiting") {
        window.history.replaceState(
          window.history.state,
          document.title,
          location.pathname,
        );
        if (outcome.kind === "waiting") setWaitingFor(outcome.returnTo);
        else setFailure(outcome.screen);
        return;
      }
      const { returnTo, outcome: flow, signOut } = outcome.result;
      if (flow === undefined) {
        /* The hand-off (PB-5461). This tab asked /me before the sign-in
           finished and holds its "not signed in" answer. RESET it — back to
           pending, no data — before moving on, so the page guard waits on a
           fresh answer ("Checking your session…") instead of reading the stale
           null and starting a second sign-in. Fired, never awaited: a failing
           /me would hold this page for its whole retry ladder, and the guard
           has its own screen for that. */
        void queryClient.resetQueries({ queryKey: ME_QUERY_KEY });
        navigate(safeAuthReturnTo(outcome.keptReturnTo ?? returnTo), {
          replace: true,
        });
        return;
      }
      // A password change: no new session either way, so nothing to wait on.
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      if (flow === "password-changed") {
        /* Stop here rather than navigating. Every session has been revoked,
           so any route would bounce straight into a fresh sign-in and the
           person would never learn whether their password actually changed
           — the one thing they came here to find out (PB-5415). */
        setPasswordChanged({ signOut: signOut ?? "incomplete" });
        return;
      }
      // A cancelled action returns to where it started with nothing changed,
      // which is an ordinary navigation like a completed sign-in's.
      navigate(safeAuthReturnTo(returnTo), { replace: true });
    });

    return () => {
      live = false;
    };
  }, [location.pathname, location.search, navigate, queryClient]);

  /** Start one sign-in from a button, or keep the screen retryable. A button
   *  is the person choosing this tab, so it takes the sign-in lease (d21). */
  const openSignIn = async (
    path: string,
    options?: BeginLoginOptions,
  ): Promise<void> => {
    if (retryInFlight.current) return;
    retryInFlight.current = true;
    setRetrying(true);
    setRetryFailed(false);
    await takeSignInLease();
    try {
      const url = await (options
        ? beginLogin(path, options)
        : beginLogin(path));
      window.location.replace(url);
    } catch {
      retryInFlight.current = false;
      setRetryFailed(true);
      setRetrying(false);
    }
  };

  /* PB-5415's buttons keep their own request. A plain new OIDC transaction can
     silently reuse both Keycloak's and Microsoft's browser SSO sessions, so a
     password-change recovery asks for account selection, at Shell home. */
  const signInAfterPasswordChange = (): void =>
    void openSignIn("/", { prompt: "select_account" });

  /* Kept as plain text: Alert would add a tinted box and a glyph under the
     panel, a look change no PR 3 row approves. */
  const retryFailedLine = retryFailed ? (
    <p className="max-w-md text-sm" role="alert">
      Could not open sign-in. Please try again.
    </p>
  ) : null;

  if (passwordChanged) {
    const revokedEverywhere = passwordChanged.signOut === "revoked";
    return (
      <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
        {productMark}
        {/* `alert`, not `status`: the person is signed out of everything and
            cannot continue without acting, so it interrupts rather than
            waiting its turn. */}
        <p className="text-lg font-medium text-pb-text" role="alert">
          Your password has changed.
        </p>
        {/* The sign-out is the SECURITY REASON for changing a password, so this
            reports what actually happened rather than what was intended. The
            revocation is identity-wide — every workspace this person belongs
            to, plus the shared sign-in — so the stronger sentence is now the
            true one. A failed leg still must not be described as success
            (PB-5415). */}
        <p className="max-w-md text-sm">
          {revokedEverywhere ? (
            <>
              You&rsquo;ve been signed out on every device. Sign in again with
              your new password.
            </>
          ) : (
            <>
              You&rsquo;ve been signed out here, but we couldn&rsquo;t confirm
              your other sessions ended. Sign in again with your new password,
              and ask an admin to sign you out everywhere.
            </>
          )}
        </p>
        <Button
          variant="secondary"
          disabled={retrying}
          onClick={signInAfterPasswordChange}
        >
          {retrying ? "Opening sign-in…" : "Sign in"}
        </Button>
        {/* Every session is revoked by now, so this button is the only way
            forward — a silent failure would strand the person entirely. */}
        {retryFailedLine}
      </div>
    );
  }

  if (waitingFor !== null) {
    return (
      <SignInElsewhereScreen
        mark={productMark}
        busy={retrying}
        onSignedIn={() => {
          // The other tab's session is this tab's too: go where it was going.
          forgetReturnTo();
          navigate(safeAuthReturnTo(waitingFor), { replace: true });
        }}
        onLeaseFree={() => {
          // Nobody is signing in after all: lead, unless another tab won.
          if (retryInFlight.current) return;
          void electSignInLeader().then((leads) => {
            if (leads) void openSignIn(waitingFor);
          });
        }}
        onSignInHere={() => void openSignIn(waitingFor)}
      >
        {retryFailedLine}
      </SignInElsewhereScreen>
    );
  }

  if (failure) {
    const { title, description, actionLabel, onAction } = failureCopy(failure, {
      signInAfterPasswordChange,
      openSignIn: (path, options) => void openSignIn(path, options),
    });
    return (
      <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
        {productMark}
        {/* The page-level failure screen: EmptyState lg (OK-33). */}
        <EmptyState
          variant="error"
          size="lg"
          title={title}
          description={description}
          action={
            <Button variant="secondary" disabled={retrying} onClick={onAction}>
              {retrying ? "Opening sign-in…" : actionLabel}
            </Button>
          }
        />
        {retryFailedLine}
      </div>
    );
  }

  /* Kept as plain text: a Spinner ring beside it is a look change no PR 3 row
     approves (OK-38 names other files). */
  return (
    <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
      {productMark}
      <p>Completing sign in…</p>
    </div>
  );
}

/** The words and the one way forward for each failure (PLAN §7). */
function failureCopy(
  failure: FailureScreen,
  actions: {
    readonly signInAfterPasswordChange: () => void;
    readonly openSignIn: (path: string, options?: BeginLoginOptions) => void;
  },
): {
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string;
  readonly onAction: () => void;
} {
  switch (failure.kind) {
    case "expired":
      return {
        title: "We couldn’t finish signing you in.",
        description: "The sign-in page was open too long or was already used.",
        actionLabel: "Try again",
        onAction: () => actions.openSignIn(failure.returnTo),
      };
    case "password-change-unconfirmed":
      return {
        title: "We couldn’t confirm your password change.",
        description:
          "If you changed it, sign in with your new password. If not, start again from Account settings.",
        actionLabel: "Sign in",
        onAction: actions.signInAfterPasswordChange,
      };
    case "password-change":
      // PB-5415's words, unchanged.
      return failure.pending
        ? {
            title: "Password change is still being confirmed.",
            description:
              "Your password may have changed. Wait a moment, then sign in with your new password.",
            actionLabel: "Sign in again",
            onAction: actions.signInAfterPasswordChange,
          }
        : {
            title: "Could not confirm password change.",
            description:
              "We could not safely finish this attempt. Sign in, then start again from Account settings.",
            actionLabel: "Sign in again",
            onAction: actions.signInAfterPasswordChange,
          };
    case "refused": {
      const words = REFUSALS[failure.code];
      return {
        title: words.title,
        description: words.description ?? failure.message,
        actionLabel: "Choose another account",
        /* `prompt=login` forces Keycloak's form and ends this browser's app
           session once the new sign-in is saved (PR 391): choosing an account
           again must not land on the refused one. Home, because the refused
           person's page means nothing to the next one. */
        onAction: () => actions.openSignIn("/", { prompt: "login" }),
      };
    }
    case "server-problem":
      return {
        title: "Something went wrong on our side.",
        description:
          "Please try again. If it keeps happening, tell your admin.",
        actionLabel: "Try again",
        onAction: () => actions.openSignIn(failure.returnTo),
      };
  }
}
